"use client";

/**
 * Hook that fetches the next upcoming match for a team from the predictor leaderboard,
 * and checks if the current user has already made a prediction for it.
 *
 * Returns { nextMatch, existingPrediction, loading, refetch }.
 * If no leaderboard is configured or no upcoming match exists, nextMatch will be null.
 */
import { useState, useEffect, useCallback } from "react";
import { useFansUnitedSDK } from "fansunited-data-layer/client";
import type MatchBasicModel from "fansunited-sdk-esm/Core/Namespaces/Football/Models/Match/MatchBasicModel";
import { useTeamPageData } from "@/views/team/context/TeamPageDataContext";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { useAuth } from "@/lib/auth/AuthContext";
import type { ExistingPrediction } from "./useMyPredictions";
export interface UseTeamNextMatchForPredictorResult {
  nextMatch: MatchBasicModel | null;
  existingPrediction: ExistingPrediction | undefined;
  loading: boolean;
  refetch: () => void;
}
export function useTeamNextMatchForPredictor(): UseTeamNextMatchForPredictorResult {
  const [nextMatch, setNextMatch] = useState<MatchBasicModel | null>(null);
  const [existingPrediction, setExistingPrediction] = useState<ExistingPrediction | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const sdk = useFansUnitedSDK();
  const {
    team
  } = useTeamPageData();
  const nativeTeamId = team.id;
  const {
    predictorLeaderboardId
  } = useTenantSettings();
  const {
    user
  } = useAuth();
  const refetch = useCallback(() => setRefetchTrigger(prev => prev + 1), []);
  useEffect(() => {
    if (!sdk || !predictorLeaderboardId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const fetchData = async () => {
      try {
        // Fetch ALL matches from the leaderboard template (no groupId = all groups)
        const allMatches: MatchBasicModel[] = (await sdk.loyalty.getTemplateMatches(predictorLeaderboardId)) || [];
        if (cancelled) return;
        const now = new Date();

        // Find the next upcoming match for this team
        const teamNextMatch = allMatches.filter(m => {
          const isTeamMatch = m.homeTeam?.id === nativeTeamId || m.awayTeam?.id === nativeTeamId;
          const isUpcoming = new Date(m.kickoffAt) > now;
          return isTeamMatch && isUpcoming;
        }).sort((a, b) => new Date(a.kickoffAt).getTime() - new Date(b.kickoffAt).getTime())[0] || null;
        if (!teamNextMatch) {
          if (!cancelled) {
            setNextMatch(null);
            setLoading(false);
          }
          return;
        }

        // Check if user has predicted this match
        let prediction: ExistingPrediction | undefined;
        if (user) {
          try {
            const response = await sdk.predictor.getMyPredictionsForMatches([teamNextMatch.id]);
            const predictions = Array.isArray(response) ? response : Array.isArray(response?.data) ? response.data : [];
            for (const pred of predictions) {
              if (pred.fixtures) {
                for (const fixture of pred.fixtures) {
                  const isCorrectScore = fixture.market === "CORRECT_SCORE" || fixture.market === "CORRECT_SCORE_ADVANCED" || fixture.market === "CORRECT_SCORE_HT";
                  if (isCorrectScore && fixture.matchId === teamNextMatch.id) {
                    const value = String(fixture.prediction?.value || "0:0");
                    const parts = value.split(":");
                    const home = parseInt(parts[0], 10);
                    const away = parseInt(parts[1], 10);
                    prediction = {
                      predictionId: pred.id,
                      homeScore: isNaN(home) ? 0 : home,
                      awayScore: isNaN(away) ? 0 : away,
                      market: fixture.market,
                      status: fixture.result?.status || "NOT_SETTLED",
                      outcome: fixture.result?.outcome || "NOT_VERIFIED",
                      points: fixture.result?.points || 0
                    };
                  }
                }
              }
            }
          } catch (err_0) {
            console.error("Failed to fetch prediction for match:", err_0);
          }
        }
        if (!cancelled) {
          setNextMatch(teamNextMatch);
          setExistingPrediction(prediction);
          setLoading(false);
        }
      } catch (err) {
        console.error("Failed to fetch leaderboard matches:", err);
        if (!cancelled) {
          setNextMatch(null);
          setLoading(false);
        }
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [sdk, predictorLeaderboardId, nativeTeamId, user, refetchTrigger]);
  return {
    nextMatch,
    existingPrediction,
    loading,
    refetch
  };
}